import { useDisclosure } from '@chakra-ui/hooks';
import { Box, Button, Flex, Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';

import { ChatMessageFormatter } from '@/components/ui/shared/chat/ChatPopup/components/ChatMessageFormatter';
import { IToastType } from '@/enums/ToastType';
import { useStores } from '@/hooks/useStores';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { useToast } from '@/hooks/useToast';
import { ChatMessages } from '@/resources/ChatMessages';

interface IMessageBubble {
  message: ChatMessages;
  mutate: () => void;
  setEditMessage: (message: ChatMessages) => void;
}

export const MessageBubble: FC<IMessageBubble> = ({ message, mutate, setEditMessage }) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;
  const { store } = useStores();
  const toast = useToast();

  const myMessage = message.senderId.toString() === user.id;

  async function handleDeleteMessage() {
    try {
      await store.request(`${ChatMessages.type}/${message.id}`, 'DELETE', {
        data: {
          type: ChatMessages.type,
          attributes: {
            channel_id: message.channelId,
          },
        },
      });

      mutate();
      onClose();
    } catch (response) {
      toast({ status: IToastType.ERROR, title: 'Error' });
    }
  }

  if (myMessage) {
    return (
      <Flex justifyContent="flex-end" alignItems="center" role="group">
        <Popover isOpen={isOpen} onClose={onClose} returnFocusOnClose={true} lazyBehavior="unmount" placement="top">
          <PopoverTrigger>
            <Button
              onClick={onToggle}
              color="white"
              variant="unstyled"
              _groupHover={{ color: 'black' }}
              size="sm"
              mr={2}
            >
              ···
            </Button>
          </PopoverTrigger>
          <PopoverContent w="100%">
            <Flex flexDirection="column" gap={1} justifyContent="flex-end" py={2}>
              <Button
                px={2}
                borderRadius={0}
                _hover={{ backgroundColor: 'gray.100' }}
                h={7}
                onClick={handleDeleteMessage}
                textAlign="left"
                fontWeight="normal"
                variant="unstyled"
                size="sm"
              >
                Delete message
              </Button>
              <Button
                px={2}
                borderRadius={0}
                _hover={{ backgroundColor: 'gray.100' }}
                h={7}
                onClick={() => setEditMessage(message)}
                fontWeight="normal"
                textAlign="left"
                variant="unstyled"
                size="sm"
              >
                Edit message
              </Button>
            </Flex>
          </PopoverContent>
        </Popover>
        <Box maxW="85%" bg="violet.50" borderRadius="8px 8px 0px 8px" px={4} py={2}>
          <ChatMessageFormatter message={message.content} />
        </Box>
      </Flex>
    );
  }

  return (
    <Flex justifyContent="flex-start">
      <Box maxW="85%" bg="gray.50" borderRadius="8px 8px 8px 0px" px={4} py={2}>
        <ChatMessageFormatter message={message.content} />
      </Box>
    </Flex>
  );
};
